import type { SkuCopyResult } from '../lib/api';

/**
 * The full report for a SKU-copy action, shown in place of the one-line toast
 * whenever anything besides "queued" or "already matches" happened —
 * blocked, a collision, a duplicate target, or a product with no website SKU
 * to copy. Modeled on SheetSync's own per-row report, the closest sibling
 * this app already has for "here's what happened to each of several items".
 */

interface Props {
  result: SkuCopyResult;
  onDismiss: () => void;
}

export function SkuCopyReport({ result, onDismiss }: Props) {
  const outcomes = result.products.flatMap((p) => p.outcomes);
  const alreadyMatching = outcomes.filter((o) => o.reason === 'already_matches').length;
  const problems = outcomes.filter((o) => o.status === 'skipped' && o.reason !== 'already_matches');
  const productsAffected = result.products.filter((p) => p.outcomes.some((o) => o.status === 'queued')).length;

  return (
    <div
      className={`alert small ${result.queued > 0 ? 'ok' : 'warn'}`}
      onClick={onDismiss}
      role="status"
      style={{ marginTop: 12 }}
    >
      {result.dryRun && <strong>Preview only — nothing was staged. </strong>}
      {result.queued} staged across {productsAffected} product{productsAffected === 1 ? '' : 's'}
      {alreadyMatching > 0 && (
        <>
          , {alreadyMatching} already matching
        </>
      )}
      {problems.length > 0 && (
        <>
          , {problems.length} skipped
        </>
      )}
      . Nothing is pushed to any marketplace until you sync the queue.
      {problems.length > 0 && (
        <ul style={{ marginBottom: 0, maxHeight: 200, overflowY: 'auto' }}>
          {problems.slice(0, 50).map((o, i) => (
            <li key={`${o.listingId}-${i}`} className="small">
              {o.channelName}: {o.message}
            </li>
          ))}
          {problems.length > 50 && <li className="small muted">…and {problems.length - 50} more</li>}
        </ul>
      )}
    </div>
  );
}
